"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ClipboardEvent, FormEvent, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, History, Inbox, Loader2, RefreshCw, Search, SearchX, X } from "lucide-react";
import {
  listWebhookDeliveries,
  type ApiError,
  type DeliveryBucket,
  type DeliveryListItem,
  type DeliveryQuery,
  type WebhookEndpoint,
} from "../../../services/merchantApi";
import { EmptyState, ErrorCard, SegmentedControl, SkeletonRows, focusRing, secondaryBtn } from "../ui";
import { formatCount, formatCountdown, truncateMiddle } from "./format";
import { EVENT_TYPE_LABEL } from "./vocab";
import DeliveryRow from "./DeliveryRow";

// ────────────────────────────────────────────────────────────────────────────
// DeliveriesTab — the delivery history of /developers/webhooks?tab=deliveries.
//
// The query string is the single source of truth for the filters (spec §5.1):
//   mode (live|test) · endpoint · status · type · session · event · from · to
// It is parsed on every render and written with navigate(…, { replace: true }),
// so filters survive a reload and can be shared as a link. Parameters this
// tab does not own (tab, …) are always preserved.
//
// Loading rules:
//   • the first load of a query shows layout-mirroring skeletons; a refetch
//     never does once rows exist (old rows are dimmed while a new filter loads);
//   • "Load more" follows the stateless id cursor; any filter change resets;
//   • page 1 is silently re-read every 15 s while any loaded row is still in
//     progress and the tab is visible, and whenever `staleKey` changes — the
//     answer is MERGED by id, never swapped for a skeleton;
//   • all list calls share one server rate limit, so a 429 anywhere starts
//     one countdown that pauses every list request until it runs out.
// ────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;
const AUTO_REFRESH_MS = 15_000;
const HISTORY_DAYS = 30;
// Read by DeliveryDetailPage so "Back to deliveries" returns to this view.
const QUERY_STORAGE_KEY = "cashtap:webhooks:deliveriesQuery";

const DELIVERY_ID_RE = /^whd_[0-9a-z]{9}[0-9a-f]{12}$/;
const EVENT_ID_RE = /^evt_(?:test_[A-Za-z0-9_-]{16}|[A-Za-z0-9_-]{24})$/;
const SESSION_ID_RE = /^cs_(?:live|test)_[A-Za-z0-9_-]{16,32}$/;
const ENDPOINT_ID_RE = /^whe_[A-Za-z0-9_-]{16}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// Ids embedded in a pasted URL or sentence (the pay page URL, a dashboard
// link, a log line). Bounded on both sides so a longer token never matches.
const EMBEDDED = {
  delivery: /(?:^|[^A-Za-z0-9_-])(whd_[0-9a-z]{9}[0-9a-f]{12})(?![A-Za-z0-9_-])/,
  event: /(?:^|[^A-Za-z0-9_-])(evt_(?:test_[A-Za-z0-9_-]{16}|[A-Za-z0-9_-]{24}))(?![A-Za-z0-9_-])/,
  session: /(?:^|[^A-Za-z0-9_-])(cs_(?:live|test)_[A-Za-z0-9_-]{16,32})(?![A-Za-z0-9_-])/,
};

const STATUS_OPTIONS: { value: DeliveryBucket; label: string }[] = [
  { value: "succeeded", label: "Delivered" },
  { value: "failed", label: "Failed" },
  { value: "in_progress", label: "In progress" },
  { value: "skipped", label: "Skipped" },
];
const STATUS_VALUES = new Set<string>(STATUS_OPTIONS.map((o) => o.value));
const IN_PROGRESS = new Set(["pending", "delivering", "retrying"]);

// Keys of the query string this tab owns (and nothing else).
type FilterKey = "mode" | "endpoint" | "status" | "type" | "session" | "event" | "from" | "to";
const NARROWING_KEYS: FilterKey[] = ["endpoint", "status", "type", "session", "event", "from", "to"];

interface Filters {
  mode: "live" | "test";
  endpoint: string;
  status: DeliveryBucket | "";
  type: string;
  session: string;
  event: string;
  from: string; // YYYY-MM-DD, local calendar day
  to: string;
}

// ─── Query string ↔ filters ────────────────────────────────────────────────

function parseFilters(search: string): Filters {
  const sp = new URLSearchParams(search);
  const pick = (key: string, re: RegExp) => {
    const v = (sp.get(key) || "").trim();
    return re.test(v) ? v : "";
  };
  const statusRaw = sp.get("status") || "";
  const typeRaw = sp.get("type") || "";
  let from = pick("from", DATE_RE);
  let to = pick("to", DATE_RE);
  if (from && Number.isNaN(localDay(from).getTime())) from = "";
  if (to && Number.isNaN(localDay(to).getTime())) to = "";
  if (from && to && from > to) [from, to] = [to, from];
  return {
    mode: sp.get("mode") === "test" ? "test" : "live",
    endpoint: pick("endpoint", ENDPOINT_ID_RE),
    status: STATUS_VALUES.has(statusRaw) ? (statusRaw as DeliveryBucket) : "",
    type: Object.prototype.hasOwnProperty.call(EVENT_TYPE_LABEL, typeRaw) ? typeRaw : "",
    session: pick("session", SESSION_ID_RE),
    event: pick("event", EVENT_ID_RE),
    from,
    to,
  };
}

// "2026-09-10" → local midnight of that calendar day.
function localDay(ymd: string, endOfDay = false): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
}

function ymdOf(date: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

// The API query for a set of filters (without cursor / limit). Its JSON is
// the identity of "this list": rows, errors and in-flight requests are all
// tagged with it so an answer can never land in the wrong view.
function toApiQuery(f: Filters): DeliveryQuery {
  return {
    mode: f.mode,
    endpointId: f.endpoint || undefined,
    status: f.status || undefined,
    eventType: f.type || undefined,
    sessionId: f.session || undefined,
    eventId: f.event || undefined,
    from: f.from ? localDay(f.from).toISOString() : undefined,
    to: f.to ? localDay(f.to, true).toISOString() : undefined,
  };
}

// ─── List merging ──────────────────────────────────────────────────────────
// Delivery ids sort ascending = newest first (inverted-time prefix), which is
// also the server's order, so plain string comparison orders rows.

interface ListState {
  key: string;
  rows: DeliveryListItem[];
  cursor: string | null;
  hasMore: boolean;
}

function sortRows(rows: DeliveryListItem[]): DeliveryListItem[] {
  return rows.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

function appendPage(prev: DeliveryListItem[], page: DeliveryListItem[]): DeliveryListItem[] {
  const byId = new Map<string, DeliveryListItem>();
  for (const r of prev) byId.set(r.id, r);
  for (const r of page) byId.set(r.id, r);
  return sortRows(Array.from(byId.values()));
}

// A fresh page 1 is authoritative for everything from the newest row down to
// its own last row: rows in that range that it no longer contains stopped
// matching the filter (e.g. a delivery left "In progress") and are dropped.
// Rows older than its last row belong to pages already loaded with "Load
// more"; they are kept, together with the old cursor.
function mergeFirstPage(
  prev: ListState,
  fresh: { deliveries: DeliveryListItem[]; nextCursor: string | null; hasMore: boolean },
): ListState {
  const rows = fresh.deliveries;
  if (!fresh.hasMore || rows.length === 0) {
    return { key: prev.key, rows: sortRows([...rows]), cursor: fresh.nextCursor, hasMore: fresh.hasMore };
  }
  const lastId = rows[rows.length - 1].id;
  const freshIds = new Set(rows.map((r) => r.id));
  const tail = prev.rows.filter((r) => r.id > lastId && !freshIds.has(r.id));
  if (tail.length === 0) {
    return { key: prev.key, rows: sortRows([...rows]), cursor: fresh.nextCursor, hasMore: fresh.hasMore };
  }
  return { key: prev.key, rows: sortRows([...rows, ...tail]), cursor: prev.cursor, hasMore: prev.hasMore };
}

// ─── Search box ────────────────────────────────────────────────────────────

type SearchResult =
  | { kind: "delivery" | "event" | "session"; id: string }
  | { kind: "invalid"; hint: string };

const SEARCH_HINT = "Enter a delivery (whd_), event (evt_) or session (cs_) id.";

function classifySearch(raw: string): SearchResult | null {
  const s = raw.trim().replace(/^["'`<(\[]+|["'`>)\].,;]+$/g, "");
  if (!s) return null;
  if (DELIVERY_ID_RE.test(s)) return { kind: "delivery", id: s };
  if (EVENT_ID_RE.test(s)) return { kind: "event", id: s };
  if (SESSION_ID_RE.test(s)) return { kind: "session", id: s };
  // A pasted URL (https://pay.cashtap.cash/c/cs_…, a dashboard link) or a
  // log line that contains exactly one kind of id.
  const embeddedDelivery = s.match(EMBEDDED.delivery);
  if (embeddedDelivery) return { kind: "delivery", id: embeddedDelivery[1] };
  const embeddedSession = s.match(EMBEDDED.session);
  if (embeddedSession) return { kind: "session", id: embeddedSession[1] };
  const embeddedEvent = s.match(EMBEDDED.event);
  if (embeddedEvent) return { kind: "event", id: embeddedEvent[1] };
  if (/^whd_/i.test(s)) return { kind: "invalid", hint: "That delivery id looks incomplete. Delivery ids are whd_ followed by 21 characters." };
  if (/^evt_/i.test(s)) return { kind: "invalid", hint: "That event id looks incomplete. Copy it from the X-CashTap-Event-Id header or the payload's id." };
  if (/^cs_/i.test(s)) return { kind: "invalid", hint: "That session id looks incomplete. Session ids start with cs_live_ or cs_test_." };
  return { kind: "invalid", hint: SEARCH_HINT };
}

// ─── Errors + rate-limit countdown ─────────────────────────────────────────

interface LoadError {
  message: string;
  requestId: string | null;
  retryAfter: number | null;
}

function toLoadError(e: unknown): LoadError {
  const err = e as ApiError | undefined;
  const rateLimited = err?.status === 429;
  return {
    message: rateLimited
      ? "Too many requests right now."
      : err?.message || "Something went wrong while loading deliveries.",
    requestId: err?.requestId || null,
    retryAfter: rateLimited ? err?.retryAfter || 60 : null,
  };
}

// Seconds left of a server-imposed wait; re-renders once per second while
// running and stops itself at 0 (so the controls re-enable on their own).
function useCountdown(): [number, (seconds: number) => void] {
  const [left, setLeft] = useState(0);
  const until = useRef(0);
  const running = left > 0;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setLeft(Math.max(0, Math.ceil((until.current - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [running]);
  const start = useCallback((seconds: number) => {
    const s = Math.max(1, Math.ceil(seconds));
    until.current = Date.now() + s * 1000;
    setLeft(s);
  }, []);
  return [left, start];
}

// ─── Component ─────────────────────────────────────────────────────────────

const controlCls =
  "w-full h-10 bg-gray-900/60 border border-gray-800/80 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#61CE70]/50 focus:bg-gray-900/80 transition-colors";
const selectCls = `${controlCls} pl-3 pr-9 appearance-none cursor-pointer truncate`;

/**
 * Delivery history tab.
 * @param wallet    identity header (from WebhooksGate)
 * @param email     identity header
 * @param endpoints the merchant's endpoints — options of the Endpoint filter
 * @param staleKey  bump after a test send / resend: page 1 is re-read (merged)
 */
export default function DeliveriesTab({
  wallet,
  email,
  endpoints,
  staleKey,
}: {
  wallet: string | null;
  email: string | null;
  endpoints: WebhookEndpoint[];
  staleKey: number;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputId = useId();
  const searchHintId = useId();

  const filters = useMemo(() => parseFilters(location.search), [location.search]);
  const queryKey = useMemo(() => JSON.stringify(toApiQuery(filters)), [filters]);
  // Stable per key: unrelated query-string changes (tab, …) do not refetch.
  const apiQuery = useMemo<DeliveryQuery>(() => JSON.parse(queryKey), [queryKey]);
  const hasNarrowing = NARROWING_KEYS.some((k) => !!filters[k]);

  // Remember the current view for the detail page's "Back to deliveries".
  useEffect(() => {
    try {
      sessionStorage.setItem(QUERY_STORAGE_KEY, location.search);
    } catch {
      /* storage unavailable (private mode): the back link falls back to the tab */
    }
  }, [location.search]);

  const updateQuery = useCallback(
    (patch: Partial<Record<FilterKey, string | null>>) => {
      const sp = new URLSearchParams(location.search);
      for (const [k, v] of Object.entries(patch)) {
        if (v) sp.set(k, v);
        else sp.delete(k);
      }
      const s = sp.toString();
      navigate({ pathname: location.pathname, search: s ? `?${s}` : "" }, { replace: true });
    },
    [location.pathname, location.search, navigate],
  );

  const clearFilters = useCallback(() => {
    const patch: Partial<Record<FilterKey, null>> = {};
    for (const k of NARROWING_KEYS) patch[k] = null;
    updateQuery(patch);
  }, [updateQuery]);

  // ── Data ───────────────────────────────────────────────────────────────
  const [list, setList] = useState<ListState | null>(null);
  const listRef = useRef<ListState | null>(null);
  listRef.current = list;
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<(LoadError & { key: string }) | null>(null);
  const [refreshError, setRefreshError] = useState<LoadError | null>(null);
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState<LoadError | null>(null);
  const [cooldown, startCooldown] = useCountdown();

  const firstSeq = useRef(0);
  const moreSeq = useRef(0);
  const firstInFlight = useRef(false);
  const lastFirstFetchAt = useRef(0);
  const pausedUntil = useRef(0);

  // mode "reset": first load of this query (skeleton when nothing to show);
  // "manual": the Refresh button (spinner, merge); "silent": auto-refresh
  // and staleKey (merge, errors stay quiet while rows are on screen).
  const fetchFirst = useCallback(
    async (mode: "reset" | "manual" | "silent") => {
      if (!wallet) return;
      const key = queryKey;
      const my = ++firstSeq.current;
      firstInFlight.current = true;
      if (mode === "reset") {
        setLoadingKey(key);
        setError(null);
        setRefreshError(null);
        // A page of the previous query must not be appended to this one.
        moreSeq.current += 1;
        setMoreLoading(false);
        setMoreError(null);
      }
      if (mode === "manual") {
        setRefreshing(true);
        setRefreshError(null);
      }
      try {
        const res = await listWebhookDeliveries(wallet, email, { ...apiQuery, limit: PAGE_SIZE });
        if (my !== firstSeq.current) return;
        lastFirstFetchAt.current = Date.now();
        setError(null);
        setRefreshError(null);
        setList((prev) =>
          prev && prev.key === key && mode !== "reset"
            ? mergeFirstPage(prev, res)
            : {
                key,
                rows: appendPage([], res.deliveries),
                cursor: res.nextCursor,
                hasMore: res.hasMore,
              },
        );
      } catch (e) {
        if (my !== firstSeq.current) return;
        const err = toLoadError(e);
        if (err.retryAfter) {
          pausedUntil.current = Date.now() + err.retryAfter * 1000;
          startCooldown(err.retryAfter);
        }
        const showing = listRef.current && listRef.current.key === key;
        if (!showing) setError({ ...err, key });
        else if (mode === "manual") setRefreshError(err);
        // silent + rows on screen: keep them, try again on the next tick.
      } finally {
        if (my === firstSeq.current) {
          firstInFlight.current = false;
          setLoadingKey(null);
          setRefreshing(false);
        }
      }
    },
    [wallet, email, queryKey, apiQuery, startCooldown],
  );

  // Initial load, and a reset on every filter / identity change.
  useEffect(() => {
    void fetchFirst("reset");
  }, [fetchFirst]);

  // The parent bumps staleKey after a test send or a resend.
  const seenStaleKey = useRef(staleKey);
  useEffect(() => {
    if (seenStaleKey.current === staleKey) return;
    seenStaleKey.current = staleKey;
    void fetchFirst("silent");
  }, [staleKey, fetchFirst]);

  const current = list && list.key === queryKey ? list : null;
  const hasInProgress = !!current && current.rows.some((r) => IN_PROGRESS.has(r.status));

  // Keep in-progress rows live: re-read page 1 every 15 s while the tab is
  // visible; catch up at once when it becomes visible again.
  useEffect(() => {
    if (!hasInProgress) return;
    const tick = () => {
      if (document.hidden || firstInFlight.current) return;
      if (Date.now() < pausedUntil.current) return;
      if (Date.now() - lastFirstFetchAt.current < AUTO_REFRESH_MS - 1000) return;
      void fetchFirst("silent");
    };
    const id = setInterval(tick, AUTO_REFRESH_MS);
    const onVisible = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [hasInProgress, fetchFirst]);

  const loadMore = useCallback(async () => {
    const cur = listRef.current;
    if (!wallet || !cur || cur.key !== queryKey || !cur.cursor || moreLoading || cooldown > 0) return;
    const key = cur.key;
    const my = ++moreSeq.current;
    setMoreLoading(true);
    setMoreError(null);
    try {
      const res = await listWebhookDeliveries(wallet, email, {
        ...apiQuery,
        limit: PAGE_SIZE,
        cursor: cur.cursor,
      });
      if (my !== moreSeq.current) return;
      setList((prev) =>
        prev && prev.key === key
          ? { key, rows: appendPage(prev.rows, res.deliveries), cursor: res.nextCursor, hasMore: res.hasMore }
          : prev,
      );
    } catch (e) {
      if (my !== moreSeq.current) return;
      const err = toLoadError(e);
      if (err.retryAfter) {
        pausedUntil.current = Date.now() + err.retryAfter * 1000;
        startCooldown(err.retryAfter);
      }
      setMoreError(err);
    } finally {
      if (my === moreSeq.current) setMoreLoading(false);
    }
  }, [wallet, email, queryKey, apiQuery, moreLoading, cooldown, startCooldown]);

  // ── Search ─────────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState("");
  const [searchHint, setSearchHint] = useState<string | null>(null);

  // The filters themselves are re-parsed from location.search on every
  // render, so a change from outside (a banner link clicked while this tab
  // is open, Back / Forward) re-syncs them and refetches; our own replace
  // writes only refetch when the parsed query really changed (`queryKey`).
  // The search hint is the one piece of local state: it described the
  // previous view, so drop it whenever the query changes.
  useEffect(() => {
    setSearchHint(null);
  }, [queryKey]);

  const applySearch = useCallback(
    (raw: string): boolean => {
      const result = classifySearch(raw);
      if (!result) {
        setSearchHint(null);
        return false;
      }
      if (result.kind === "invalid") {
        setSearchHint(result.hint);
        return false;
      }
      setSearchHint(null);
      setSearchText("");
      if (result.kind === "delivery") {
        // A push, not a replace: Back returns to this list.
        navigate(`/developers/webhooks/deliveries/${encodeURIComponent(result.id)}`);
        return true;
      }
      // An id is already as specific as it gets: drop the other narrowing
      // filters so a stale status/date filter cannot hide the match.
      const patch: Partial<Record<FilterKey, string | null>> = {};
      for (const k of NARROWING_KEYS) patch[k] = null;
      if (result.kind === "event") {
        patch.event = result.id;
        // Dashboard test events (evt_test_…) only exist in the test history.
        patch.mode = result.id.startsWith("evt_test_") ? "test" : null;
      } else {
        patch.session = result.id;
      }
      updateQuery(patch);
      return true;
    },
    [navigate, updateQuery],
  );

  const onSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!searchText.trim()) {
      setSearchHint(SEARCH_HINT);
      return;
    }
    applySearch(searchText);
  };

  // Pasting a recognisable id applies it at once — no Enter needed.
  const onSearchPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    const result = classifySearch(pasted);
    if (result && result.kind !== "invalid") {
      e.preventDefault();
      applySearch(pasted);
    }
  };

  // ── Options ────────────────────────────────────────────────────────────
  const endpointOptions = useMemo(() => {
    const opts = endpoints.map((ep) => ({ value: ep.id, label: endpointLabel(ep) }));
    // A link can point at an endpoint that is not in the list (deleted, or
    // the list is still loading): keep the value selectable and visible.
    if (filters.endpoint && !endpoints.some((ep) => ep.id === filters.endpoint)) {
      opts.push({ value: filters.endpoint, label: `Endpoint ${truncateMiddle(filters.endpoint, 8, 4)}` });
    }
    return opts;
  }, [endpoints, filters.endpoint]);

  const today = new Date();
  const maxDate = ymdOf(today);
  const minDate = ymdOf(new Date(today.getFullYear(), today.getMonth(), today.getDate() - HISTORY_DAYS));

  // ── Render ─────────────────────────────────────────────────────────────
  const stale = list && list.key !== queryKey ? list : null;
  const listError = !current && error && error.key === queryKey ? error : null;
  const busy = loadingKey === queryKey || refreshing;
  const testMode = filters.mode === "test";

  let body: ReactNode;
  if (current) {
    if (current.rows.length === 0) {
      body = (
        <div className="p-4 sm:p-6">
          {hasNarrowing ? (
            <EmptyState
              icon={SearchX}
              title="No deliveries match these filters."
              hint={noMatchHint(filters)}
              action={
                <button type="button" onClick={clearFilters} className={secondaryBtn}>
                  <X className="w-3.5 h-3.5" /> Clear filters
                </button>
              }
            />
          ) : testMode ? (
            <EmptyState
              icon={Inbox}
              title="No test deliveries yet."
              hint="Use “Send test” on an endpoint. Test events show up here, never under Live events."
            />
          ) : (
            <EmptyState
              icon={Inbox}
              title="No deliveries yet."
              hint="Send a test event from an endpoint, or create a checkout session."
            />
          )}
        </div>
      );
    } else {
      body = (
        <ul className="divide-y divide-gray-800/50">
          {current.rows.map((d) => (
            <DeliveryRow key={d.id} delivery={d} showEndpoint={!filters.endpoint} />
          ))}
        </ul>
      );
    }
  } else if (listError) {
    body = (
      <div className="p-4 sm:p-6">
        <ErrorCard
          title="Couldn't load deliveries"
          message={
            listError.retryAfter && cooldown > 0
              ? `${listError.message} Try again in ${formatCountdown(cooldown)}.`
              : listError.message
          }
          requestId={listError.requestId}
          onRetry={cooldown > 0 ? undefined : () => void fetchFirst("reset")}
        />
      </div>
    );
  } else if (stale && stale.rows.length > 0) {
    // A new filter is loading: keep the old rows (dimmed, inert) instead of
    // flashing a skeleton.
    body = (
      <ul className="divide-y divide-gray-800/50 opacity-40 pointer-events-none select-none" aria-hidden="true">
        {stale.rows.slice(0, PAGE_SIZE).map((d) => (
          <DeliveryRow key={d.id} delivery={d} showEndpoint={!filters.endpoint} />
        ))}
      </ul>
    );
  } else {
    body = <SkeletonRows rows={6} />;
  }

  const countLine = current
    ? current.rows.length === 0
      ? `No ${testMode ? "test " : ""}deliveries · history is kept for ${HISTORY_DAYS} days`
      : `Showing ${formatCount(current.rows.length)} ${testMode ? "test " : ""}${
          current.rows.length === 1 ? "delivery" : "deliveries"
        } · history is kept for ${HISTORY_DAYS} days`
    : listError
      ? "Couldn't load deliveries"
      : "Loading deliveries…";

  return (
    <div className="rounded-2xl border border-gray-800/80 bg-gradient-to-br from-[#0F1419]/70 to-[#0B0F16]/70 overflow-hidden">
      {/* ── Header + filters ─────────────────────────────────────────── */}
      <div className="p-4 sm:p-6 border-b border-gray-800/60">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-[#61CE70]/10 border border-[#61CE70]/20 flex items-center justify-center shrink-0">
            <History className="w-4 h-4 text-[#61CE70]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold">Delivery history</p>
            <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">
              {testMode
                ? "Test sends from this dashboard. They never affect endpoint health or send alert emails."
                : "Every event CashTap sent to your endpoints, newest first."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void fetchFirst(current ? "manual" : "reset")}
            disabled={busy || cooldown > 0 || !wallet}
            aria-label="Refresh deliveries"
            title={cooldown > 0 ? `Try again in ${formatCountdown(cooldown)}` : "Refresh"}
            className={`shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/50 hover:bg-gray-700/60 active:scale-95 text-gray-300 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`}
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="space-y-3">
          {/* Mode + search */}
          <div className="flex flex-col sm:flex-row gap-3">
            <SegmentedControl<"live" | "test">
              value={filters.mode}
              onChange={(next) => updateQuery({ mode: next === "test" ? "test" : null })}
              options={[
                { value: "live", label: "Live events" },
                { value: "test", label: "Test events" },
              ]}
              ariaLabel="Delivery mode"
              fullWidth
            />
            <form onSubmit={onSearchSubmit} className="relative flex-1 min-w-0" role="search">
              <label htmlFor={searchInputId} className="sr-only">
                Find a delivery, event or session by id
              </label>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              <input
                id={searchInputId}
                type="text"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  if (searchHint) setSearchHint(null);
                }}
                onPaste={onSearchPaste}
                placeholder="Find a whd_, evt_ or cs_ id"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="search"
                aria-invalid={searchHint ? true : undefined}
                aria-describedby={searchHint ? searchHintId : undefined}
                className={`${controlCls} pl-10 ${searchText ? "pr-9" : "pr-3"} font-mono placeholder:font-sans`}
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchText("");
                    setSearchHint(null);
                  }}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-lg text-gray-500 hover:text-white"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </form>
          </div>
          {searchHint && (
            <p id={searchHintId} role="alert" className="-mt-1 text-xs text-amber-300">
              {searchHint}
            </p>
          )}

          {/* Endpoint / status / event type */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SelectBox
              label="Filter by endpoint"
              value={filters.endpoint}
              onChange={(v) => updateQuery({ endpoint: v })}
              allLabel="All endpoints"
              options={endpointOptions}
            />
            <SelectBox
              label="Filter by status"
              value={filters.status}
              onChange={(v) => updateQuery({ status: v })}
              allLabel="All statuses"
              options={STATUS_OPTIONS}
            />
            <SelectBox
              label="Filter by event type"
              value={filters.type}
              onChange={(v) => updateQuery({ type: v })}
              allLabel="All event types"
              options={Object.keys(EVENT_TYPE_LABEL).map((t) => ({ value: t, label: t }))}
            />
          </div>

          {/* Date range + clear */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 sm:max-w-md">
              <DateBox
                label="From"
                value={filters.from}
                min={minDate}
                max={filters.to || maxDate}
                onChange={(v) => updateQuery({ from: v })}
              />
              <DateBox
                label="To"
                value={filters.to}
                min={filters.from || minDate}
                max={maxDate}
                onChange={(v) => updateQuery({ to: v })}
              />
            </div>
            {hasNarrowing && (
              <button
                type="button"
                onClick={clearFilters}
                className={`h-10 px-3 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-gray-400 hover:text-white bg-gray-900/60 border border-gray-800/80 rounded-xl transition-colors ${focusRing}`}
              >
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
          </div>

          {/* Id filters have no select — show them as removable chips. */}
          {(filters.session || filters.event) && (
            <div className="flex flex-wrap gap-2">
              {filters.session && (
                <IdChip label="Session" id={filters.session} onRemove={() => updateQuery({ session: null })} />
              )}
              {filters.event && (
                <IdChip label="Event" id={filters.event} onRemove={() => updateQuery({ event: null })} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Count line ───────────────────────────────────────────────── */}
      <div className="px-4 sm:px-6 py-2.5 border-b border-gray-800/60 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <p className="text-xs text-gray-500 tabular-nums" aria-live="polite">
          {countLine}
        </p>
        {hasInProgress && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
            <span className="w-1.5 h-1.5 rounded-full bg-[#61CE70] animate-pulse" aria-hidden="true" />
            Updating automatically
          </span>
        )}
      </div>
      {refreshError && current && (
        <p role="alert" className="px-4 sm:px-6 py-2 text-xs text-red-300 border-b border-gray-800/60 bg-red-500/5">
          Couldn't refresh: {refreshError.message}
          {refreshError.retryAfter && cooldown > 0 ? ` Try again in ${formatCountdown(cooldown)}.` : ""}
        </p>
      )}

      {/* ── Rows ─────────────────────────────────────────────────────── */}
      <div aria-busy={busy || undefined}>{body}</div>

      {/* ── Pagination ───────────────────────────────────────────────── */}
      {current && current.rows.length > 0 && (
        <div className="px-4 sm:px-6 py-4 border-t border-gray-800/60 flex flex-col items-center gap-2">
          {current.hasMore ? (
            <>
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={moreLoading || cooldown > 0}
                className={`${secondaryBtn} px-4 py-2 ${focusRing}`}
              >
                {moreLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5" />
                )}
                {moreLoading
                  ? "Loading…"
                  : cooldown > 0
                    ? `Try again in ${formatCountdown(cooldown)}`
                    : "Load more"}
              </button>
              {moreError && !(moreError.retryAfter && cooldown > 0) && (
                <p role="alert" className="text-xs text-red-300 text-center">
                  Couldn't load more: {moreError.message}
                </p>
              )}
            </>
          ) : (
            <p className="text-[11px] text-gray-600">End of history ({HISTORY_DAYS} days)</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Pieces ────────────────────────────────────────────────────────────────

function noMatchHint(f: Filters): string {
  if (f.session && f.session.startsWith("cs_test_") && f.mode === "live") {
    return "Dashboard test events use cs_test_ session ids — try Test events.";
  }
  if (f.session || f.event) return "Deliveries are kept for 30 days. Check the id, or try the other mode.";
  return "Try a different endpoint, status, event type or date range.";
}

function endpointLabel(ep: WebhookEndpoint): string {
  let path = "";
  try {
    const u = new URL(ep.url);
    path = u.pathname === "/" ? "" : u.pathname;
  } catch {
    /* keep host only */
  }
  const base = `${ep.urlHost || ep.url}${path}`;
  const shown = base.length > 52 ? `${base.slice(0, 36)}…${base.slice(-14)}` : base;
  const state = ep.health === "disabled" ? " (disabled)" : !ep.enabled ? " (paused)" : "";
  return `${shown}${state}`;
}

function SelectBox({
  label,
  value,
  onChange,
  allLabel,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string | null) => void;
  allLabel: string;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="relative min-w-0">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value || null)}
        aria-label={label}
        className={`${selectCls} ${value ? "border-[#61CE70]/30" : ""}`}
      >
        <option value="" className="bg-gray-900">
          {allLabel}
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-gray-900">
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
    </div>
  );
}

function DateBox({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: string;
  min: string;
  max: string;
  onChange: (next: string | null) => void;
}) {
  return (
    <label className="relative block min-w-0">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 pointer-events-none">
        {label}
      </span>
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const v = e.target.value;
          onChange(DATE_RE.test(v) ? v : null);
        }}
        aria-label={`${label} date`}
        className={`${controlCls} pl-14 pr-2 [color-scheme:dark] ${value ? "border-[#61CE70]/30" : ""}`}
      />
    </label>
  );
}

function IdChip({ label, id, onRemove }: { label: string; id: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 max-w-full pl-2.5 pr-1 py-1 rounded-lg bg-[#61CE70]/10 border border-[#61CE70]/25 text-[#61CE70] text-xs">
      <span className="font-sans text-[#61CE70]/70 shrink-0">{label}</span>
      <span className="font-mono truncate min-w-0" title={id}>
        {truncateMiddle(id, 12, 6)}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label.toLowerCase()} filter`}
        className={`shrink-0 p-1 rounded-md hover:bg-[#61CE70]/15 transition-colors ${focusRing}`}
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
